import Widget from "./Widget";
import WidgetDashboard from "./WidgetDashboard";
import React, { Component } from "react";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import internalStateService from "./InternalStateService";
import utilityService from "./UtilityService";
import getWindowSize from "../utils/windowSize";
import debounce from "@tinqin/tinqin-utils/src/dom/debounce";
import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

export class ConnectedDashboard extends Component {
    constructor(props) {
        super(props);
        this.updateScreenSize = debounce(this.updateScreenSize.bind(this), 300);
    }

    componentDidMount() {
        window.addEventListener("resize", this.updateScreenSize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.updateScreenSize);
    }

    componentDidUpdate(prevProps) {
        if (this.props.reloadOnResize && this.props.screenSize !== prevProps.screenSize) {
            this.props.reloadCurrentDashboard();
        }
    }

    updateScreenSize() {
        let windowSize = getWindowSize();
        if (windowSize.size !== this.props.screenSize) {
            this.props.dispatchUpdateWindowSize();
        }
    }

    buildHead() {
        let headTags = [];
        if (this.props.title) {
            headTags.push(<title>{this.props.title}</title>);
        }

        if (this.props.meta) {
            this.props.meta.forEach(metaProps => {
                headTags.push(<meta {...metaProps} />);
            });
        }
        return headTags;
    }

    overrideHtmlAttributes() {
        let documentHtml = document.body.parentElement;
        if (this.props.htmlPrefix) {
            documentHtml.setAttribute("prefix", this.props.htmlPrefix);
        } else if (documentHtml.hasAttribute("prefix")) {
            documentHtml.removeAttribute("prefix");
        }
        if (this.props.htmlLang) {
            documentHtml.setAttribute("lang", this.props.htmlLang);
        } else if (documentHtml.hasAttribute("lang")) {
            documentHtml.removeAttribute("lang");
        }
    }

    buildWidgetRegions() {
        let widgetsByRegion = {};

        this.props.widgets.forEach((widgetProps = {}, index) => {
            let DashboardWidget = Widget;
            let widgetProperties = Object.assign({}, widgetProps);
            if (widgetProperties.hasOwnProperty("type") && widgetProps.type === "widgetContainer") {
                DashboardWidget = WidgetDashboard;
                widgetProperties.parentWidget = this.props.widgetId;
            }
            let key = widgetProps.id || index;
            //default dashboard region
            let widgetRegion = widgetProperties.region || "main";
            //initialize widget regions
            widgetsByRegion[widgetRegion] = widgetsByRegion[widgetRegion] || [];
            //widgets may have specific position
            if (typeof widgetProperties.position === "undefined") {
                widgetsByRegion[widgetRegion].push(
                    <DashboardWidget key={key} {...widgetProperties} />
                );
            } else {
                widgetsByRegion[widgetRegion].splice(
                    Math.max(widgetProperties.position - 1, 0),
                    0,
                    <DashboardWidget key={key} {...widgetProperties} />
                );
            }
        });

        return widgetsByRegion;
    }

    getTemplate() {
        let templateId;
        let template;
        //template can be an object
        if (typeof this.props.template === "string") {
            templateId = this.props.template;
        } else {
            templateId = this.props.template.id;
        }
        let templates = internalStateService.getDashboardTemplates();
        template = templates[templateId];

        if (!template) {
            WEBUILogs.error("Unknown dashboard template: " + templateId);
            WEBUILogs.warn("Default template will be render!");
            return templates["DefaultTemplate"];
        }

        return template;
    }

    render() {
        WEBUILogs.log("Dashboard " + this.props.id + " rendered.");
        if (!this.props.widgets || !this.props.template) {
            return <span data-no-content="true" />;
        }
        let DashboardTemplate = this.getTemplate();
        this.overrideHtmlAttributes();
        return (
            <React.Fragment>
                <Helmet>{this.buildHead()}</Helmet>
                <DashboardTemplate
                    dashboardId={this.props.id}
                    template={this.props.template}
                    regions={this.buildWidgetRegions()}
                />
            </React.Fragment>
        );
    }
}

export default connect(
    //mapStateToProps
    (state, ownProps) => {
        let currentAddress = state.getIn(["routes", "current"]);
        let screenSize = state.getIn(["windowSize", "size"]);
        let dashboardDescription = state
            .getIn(["dashboards"])
            .find(dashboards => dashboards.get("address") === currentAddress);
        return {
            dashboardDescription,
            screenSize
        };
    },
    //mapDispatchToProps
    utilityService.getMapDispatchToProps({
        reloadCurrentDashboard: "dashboard.reloadCurrentDashboard",
        dispatchUpdateWindowSize: "windowSize.dispatchUpdateWindowSize"
    }),
    //mergeProps
    (stateProps, dispatchProps, ownProps) => {
        let immutableDescription = stateProps.dashboardDescription;
        let dashboardDescription =
            immutableDescription && immutableDescription.toJSON
                ? immutableDescription.toJSON()
                : {};
        let { id, config = {}, data = {} } = dashboardDescription;
        //config["widgets"] required only, data["widgets"] is used for actions like initDashboard
        //let { widgets, template, reloadOnResize } = config;
        let { template, reloadOnResize } = config;
        let { widgets = [] } = data;
        let widgetAttributes = [];
        if (config.widgets && config.widgets.length) {
            config.widgets.forEach((widgetProps = {}) => {
                let widgetId = widgetProps.id;
                let widgetsData = widgets.find(widgetData => {
                    return widgetData.id === widgetId;
                });
                //in case widget has no data skip rendering
                //currently with a flag only
                //usecase: when there is a cached widget which is not part of the data for the next page
                //but is part of a generic config for all pages
                if (!widgetsData && widgetProps.skipRenderNoData){
                    return;
                }
                let widgetProperties = Object.assign({}, widgetProps, widgetsData);
                widgetAttributes.push(widgetProperties);
            });
        }

        let seoProps = {
            htmlPrefix: config.htmlPrefix,
            htmlLang: config.htmlLang || internalStateService.getGlobalProperty("locale"),
            title: config.title,
            meta: config.meta
        };
        return Object.assign(
            {
                id,
                widgets: widgetAttributes,
                template,
                reloadOnResize,
                screenSize: stateProps.screenSize
            },
            seoProps,
            ownProps,
            dispatchProps
        );
    },
    //options
    {
        areStatePropsEqual: (next, prev) => {
            let descriptionEqual = next.dashboardDescription === prev.dashboardDescription;
            let screenSizeEqual = next.screenSize === prev.screenSize;
            return descriptionEqual && screenSizeEqual;
        }
    }
)(ConnectedDashboard);
